import { Mail } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { LOGIN } from "@/shared/constants/testIds";

import { callbackUrl } from "../client";
import { useStytchClient } from "../hooks/useSession";
import { authErrorMessage, failureReason } from "../types";
import AuthFrame from "./AuthFrame";
import BrandMark from "./BrandMark";

import type { AuthError, AuthMode } from "../types";

/* `/login` and `/signup` are this one component. Both call Stytch's
   login-or-create methods — the backend decides who is new — so only the copy
   and the cross-link differ (00-PLAN.md). */

const COPY = {
  login: {
    title: "Welcome back",
    subtitle: "Sign in with your Definable account.",
    footer: "New to Definable?",
    footerLink: "Create an account",
    footerTo: "/signup",
  },
  signup: {
    title: "Create your account",
    subtitle: "Production AI agents, in minutes.",
    footer: "Already have an account?",
    footerLink: "Sign in",
    footerTo: "/login",
  },
} as const;

// Onboarding.tsx's h-11 field and advance button, the only other chrome-less
// page in the app. DESIGN.md §5 recipes at the size a 400px card needs.
const INPUT =
  "w-full h-11 px-3.5 rounded-[10px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] text-[15px] outline-none focus:ring-[var(--hairline-focus)] placeholder:text-[var(--text-quaternary)] tracking-[-0.005em]";
const PRIMARY =
  "press w-full h-11 rounded-[10px] text-[13.5px] font-medium bg-[var(--brand)] text-[var(--brand-foreground)] hover:bg-[var(--brand-hover)] flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:pointer-events-none";
const PROVIDER =
  "press w-full h-11 rounded-[10px] ring-1 ring-[var(--hairline-strong)] bg-[var(--surface)] text-[13.5px] font-medium text-[var(--text-primary)] hover:bg-[var(--surface-sunk)] hover:ring-[var(--hairline-focus)] flex items-center justify-center gap-2 disabled:opacity-40 disabled:pointer-events-none";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_AFTER_S = 30;

export default function AuthPage({ mode }: { mode: AuthMode }) {
  const stytch = useStytchClient();
  const [params] = useSearchParams();
  const copy = COPY[mode];

  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState<"google" | "github" | "email" | null>(null);
  // Seeded from `?error=` so a failed callback explains itself here rather than
  // dumping the user on a pristine login page. `?reason=` is the SDK's own words,
  // carried over by AuthCallbackPage.
  const [error, setError] = useState<AuthError | null>(() => {
    const text = authErrorMessage(params.get("error"));
    return text ? { text, reason: params.get("reason") } : null;
  });
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const startOAuth = async (provider: "google" | "github") => {
    setError(null);
    setBusy(provider);
    const url = callbackUrl();
    try {
      // Same URL for both: Stytch tells us which it was via `stytch_token_type`.
      await stytch.oauth[provider].start({ login_redirect_url: url, signup_redirect_url: url });
    } catch (e) {
      console.error(e);
      setBusy(null);
      setError({
        text: `We could not reach ${provider === "google" ? "Google" : "GitHub"}. Try again.`,
        reason: failureReason(e),
      });
    }
  };

  const sendMagicLink = async (address: string) => {
    setError(null);
    setBusy("email");
    const url = callbackUrl();
    try {
      await stytch.magicLinks.email.loginOrCreate(address, {
        login_magic_link_url: url,
        signup_magic_link_url: url,
      });
      setSentTo(address);
      setCooldown(RESEND_AFTER_S);
    } catch (e) {
      console.error(e);
      setError({
        text: "We could not send that email. Check the address and try again.",
        reason: failureReason(e),
      });
    } finally {
      setBusy(null);
    }
  };

  const emailOk = EMAIL_RE.test(email.trim());

  return (
    <AuthFrame>
      <div className="fade-in-up w-full max-w-[400px] rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.06)] p-6">
        {sentTo ? (
          <div data-testid={LOGIN.sentState} className="fade-in flex flex-col items-center gap-3 py-2">
            <div className="w-10 h-10 rounded-[12px] bg-[var(--surface-2)] ring-1 ring-[var(--hairline)] flex items-center justify-center">
              <Mail className="w-[18px] h-[18px] text-[var(--text-tertiary)]" strokeWidth={1.5} />
            </div>
            <div className="text-[14px] font-medium tracking-[-0.005em]">Check your inbox</div>
            <div className="text-[12px] text-[var(--text-tertiary)] text-center">
              We sent a single-use sign-in link to{" "}
              <span className="font-mono text-[11.5px] text-[var(--text-secondary)]">{sentTo}</span>.
            </div>
            <button
              className="press mt-2 h-8 px-3.5 rounded-[8px] ring-1 ring-[var(--hairline-strong)] bg-[var(--surface)] text-[12px] text-[var(--text-primary)] hover:bg-[var(--surface-sunk)] hover:ring-[var(--hairline-focus)] disabled:opacity-40 disabled:pointer-events-none"
              disabled={cooldown > 0 || busy === "email"}
              onClick={() => sendMagicLink(sentTo)}
            >
              {cooldown > 0 ? (
                <>
                  Resend in <span className="font-mono tabular-nums">{cooldown}</span>s
                </>
              ) : (
                "Resend link"
              )}
            </button>
            <button
              className="press text-[12px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              onClick={() => {
                setSentTo(null);
                setCooldown(0);
              }}
            >
              Use a different email
            </button>
          </div>
        ) : (
          <>
            <h1 className="text-[22px] leading-[1.2] font-semibold tracking-[-0.01em]">{copy.title}</h1>
            <p className="mt-1.5 text-[13px] leading-[1.55] text-[var(--text-tertiary)]">{copy.subtitle}</p>

            {error && (
              <div className="mt-4 rounded-[10px] px-3 py-2 text-[12px] leading-[1.5] bg-[var(--danger-bg)] text-[var(--danger)] ring-1 ring-[var(--danger-ring)]">
                {error.text}
                {error.reason && (
                  <div className="mt-1 font-mono text-[11px] opacity-70">{error.reason}</div>
                )}
              </div>
            )}

            <div className="mt-5 flex flex-col gap-2">
              <button
                data-testid={LOGIN.googleButton}
                className={PROVIDER}
                disabled={!!busy}
                onClick={() => startOAuth("google")}
              >
                <BrandMark provider="google" />
                Continue with Google
              </button>
              <button
                data-testid={LOGIN.githubButton}
                className={PROVIDER}
                disabled={!!busy}
                onClick={() => startOAuth("github")}
              >
                <BrandMark provider="github" />
                Continue with GitHub
              </button>
            </div>

            <div className="my-5 flex items-center gap-3">
              <div className="flex-1 border-t hairline" />
              <span className="section-label">or</span>
              <div className="flex-1 border-t hairline" />
            </div>

            <form
              className="flex flex-col gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (emailOk && !busy) void sendMagicLink(email.trim());
              }}
            >
              <input
                data-testid={LOGIN.emailInput}
                className={INPUT}
                type="email"
                autoComplete="email"
                aria-label="Email address"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button
                data-testid={LOGIN.magicLinkButton}
                type="submit"
                className={PRIMARY}
                disabled={!emailOk || !!busy}
              >
                {busy === "email" ? "Sending…" : "Send magic link"}
              </button>
            </form>

            <p className="mt-5 text-[12px] text-[var(--text-tertiary)] text-center">
              {copy.footer}{" "}
              <Link
                data-testid={LOGIN.registerLink}
                to={copy.footerTo}
                className="press text-[var(--text-primary)] font-medium hover:underline"
              >
                {copy.footerLink}
              </Link>
            </p>
          </>
        )}
      </div>
    </AuthFrame>
  );
}
